import { memo } from "react";
import { GRID_ACTIONS_COLUMN_TYPE, GridRowParams } from "@mui/x-data-grid";
import type { ResourceResponse } from "types";
import { TableGridMode } from "consts/table";
import { FilterVariant } from "consts/filters";
import { Filters, MenuItem } from "consts/openBanking";
import {
    selectOpenBankingResourceSlice,
    selectIsOpenBankingResourceLoading
} from "features/openBanking/selectors";
import { useIbanXSBanksCountriesPivotActions } from "features/openBanking/actions";
import type { IbanXsBanksCountriesPivot } from "features/openBanking/types";
import { massUpdateIbanXsBanksCountriesPivot } from "features/openBanking/api";
import useIbanXSBanksCountriesPivot from "./useIbanXSBanksCountriesPivot";
import IbanXSBanksCountriesPivotDetail from "./IbanXSBanksCountriesPivotDetail";
import IbanXSBanksCountriesPivotToolbar from "./IbanXSBanksCountriesPivotToolbar";
import { DeleteIbanXSBanksCountriesPivot } from "./actions";
import {
    OpenBankingResource,
    EditOpenBankingResource,
    LogoUrl,
    useToggleEnabledOpenBankingResource,
    useOpenBankingResourceSelectionModel,
    MassUpdateOpenBankingResource
} from "../shared";

const IbanXSBanksCountriesPivot = () => {
    const {
        updateIbanXSBanksCountriesPivot
    } = useIbanXSBanksCountriesPivotActions();

    const [selectionModel, onSelectionModelChange] = useOpenBankingResourceSelectionModel();

    const simpleSwitchProps = useToggleEnabledOpenBankingResource({
        handleChangeRequest: changePayload => {
            const [request] = updateIbanXSBanksCountriesPivot(changePayload);

            return request as Promise<ResourceResponse<IbanXsBanksCountriesPivot>>;
        }
    });

    return (
        <OpenBankingResource
            BreadcrumbsProps={{
                heading: 'Iban XS Banks Countries Pivot',
                action: (
                    <MassUpdateOpenBankingResource
                        selectionModel={selectionModel}
                        onSelectionModelChange={onSelectionModelChange}
                        onConfirm={iso2Countries => massUpdateIbanXsBanksCountriesPivot({
                            iso2: iso2Countries.join(','),
                            connectorIds: selectionModel.join(',')
                        })}
                        ModalProps={{
                            titleSlot: 'Mass Update Iban XS Banks Countries Pivot'
                        }}
                    >
                        Mass Update Iban XS Banks Countries Pivot
                    </MassUpdateOpenBankingResource>
                )
            }}
            ResourceProps={{
                disableSelectionOnClick: true,
                checkboxSelection: true,
                name: 'Iban XS Banks Countries Pivot',
                useResource: useIbanXSBanksCountriesPivot,
                mode: TableGridMode.OpenBankingIbanXSBanksCountriesPivot,
                selectionModel,
                onSelectionModelChange,
                selectIsTableUninitialized: () => true,
                // selectIsOpenBankingResourceUninitialized(MenuItem.IbanXSBanksCountriesPivot),
                selectIsTableLoading: selectIsOpenBankingResourceLoading(MenuItem.IbanXSBanksCountriesPivot),
                selectTableDataSlice: selectOpenBankingResourceSlice(MenuItem.IbanXSBanksCountriesPivot),
                Toolbar: IbanXSBanksCountriesPivotToolbar,
                Detail: IbanXSBanksCountriesPivotDetail,
                CellProps: {
                    [Filters.isEnabled]: simpleSwitchProps,
                    [Filters.shouldCreditOnRedirect]: simpleSwitchProps
                },
                columns: [
                    {
                        field: Filters.logoUrl,
                        headerName: 'Logo',
                        minWidth: 160,
                        renderCell: LogoUrl,
                        headerAlign: 'center',
                        align: 'center'
                    },
                    {
                        field: GRID_ACTIONS_COLUMN_TYPE,
                        type: GRID_ACTIONS_COLUMN_TYPE,
                        getActions: (params: GridRowParams) => [
                            <EditOpenBankingResource
                                key='edit'
                                {...params}
                            />,
                            <DeleteIbanXSBanksCountriesPivot
                                key='delete'
                                {...params}
                            />
                        ]
                    }
                ],
                filterTypes: new Map<FilterVariant, string[]>([
                    [FilterVariant.SingleOption, [Filters.isEnabled]]
                ])
            }}
        />
    );
};

export default memo(IbanXSBanksCountriesPivot);
